import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Chip, Icons, XStack } from '@usepepper/sprout-kit-ui';
import { FilterDropdownMenu } from '@/experiences/sales-portal/components/filter-dropdown-menu';
import { ManagerFilterId } from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';
import { ProspectsManagerRepFilter } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-rep-filter';
import type { ChatProspectStage } from '@/types';
import type { FilterDropdownOption } from '@/experiences/sales-portal/components/filter-dropdown-menu';
import type { SalesRepFilterOption } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-rep-filter';

interface ProspectsManagerFiltersProps {
  isRepFilterLoading: boolean;
  onToggleRep: (repUUID: string) => void;
  onToggleStage: (stage: ChatProspectStage) => void;
  salesRepOptions: SalesRepFilterOption[];
  stageOptions: FilterDropdownOption<ChatProspectStage>[];
}

export const ProspectsManagerFilters: React.FC<
  ProspectsManagerFiltersProps
> = ({
  isRepFilterLoading,
  onToggleRep,
  onToggleStage,
  salesRepOptions,
  stageOptions,
}) => {
  const [openFilter, setOpenFilter] = useState<ManagerFilterId | null>(null);

  const stageCount = stageOptions.filter((option) => option.selected).length;
  const repCount = salesRepOptions.filter((option) => option.selected).length;

  const renderTrigger = (
    label: string,
    count: number,
    onPress?: () => void,
  ) => (
    <Chip
      accessibilityLabel={label}
      circular
      pressable
      selected={count > 0}
      {...(onPress ? { onPress } : {})}
    >
      <Chip.Text>{count > 0 ? `${label} ${count}` : label}</Chip.Text>
      <Chip.Icon>
        <Icons.ChevronDown />
      </Chip.Icon>
    </Chip>
  );

  return (
    <XStack gap="$8" justifyContent="flex-start">
      <FilterDropdownMenu
        isOpen={openFilter === ManagerFilterId.Stage}
        onOpenChange={(open) =>
          setOpenFilter(open ? ManagerFilterId.Stage : null)
        }
        onToggle={onToggleStage}
        options={stageOptions}
        title={i18n.t('sales_portal.prospects.manager.all_stages')}
        trigger={renderTrigger(
          i18n.t('sales_portal.prospects.manager.stages'),
          stageCount,
          () =>
            setOpenFilter(
              openFilter === ManagerFilterId.Stage
                ? null
                : ManagerFilterId.Stage,
            ),
        )}
      />
      <ProspectsManagerRepFilter
        isOpen={openFilter === ManagerFilterId.Rep}
        onOpenChange={(open) =>
          setOpenFilter(
            open && !isRepFilterLoading ? ManagerFilterId.Rep : null,
          )
        }
        onToggle={onToggleRep}
        options={salesRepOptions}
        trigger={renderTrigger(
          i18n.t('sales_portal.prospects.manager.sales_reps'),
          repCount,
        )}
      />
    </XStack>
  );
};
